import { useCallback, useState } from "react";

/** セクション ID → 閉じているか（記録の無いセクションは既定値に従う） */
type CollapsedMap = Record<string, boolean>;

const loadCollapsedMap = (storageKey: string): CollapsedMap => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // 旧形式（閉じたセクション ID の配列）も読めるようにする
    if (Array.isArray(parsed)) {
      return Object.fromEntries(
        parsed
          .filter((id): id is string => typeof id === "string")
          .map((id) => [id, true]),
      );
    }
    if (parsed && typeof parsed === "object") {
      return Object.fromEntries(
        Object.entries(parsed).filter(
          (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
        ),
      );
    }
    return {};
  } catch {
    return {};
  }
};

/**
 * 共有ノート / 共有ライブラリのサイドバーで、セクション（見出し行）ごとの
 * 開閉状態を管理する。ユーザーが開閉したセクションはその状態を localStorage
 * に保存し、まだ触っていないセクションは `defaultCollapsed` に従う
 * （複数セクションを並べるスタンドアロン表示では既定で閉じる等）。
 */
const useCollapsedSections = (
  storageKey: string,
  defaultCollapsed: boolean,
) => {
  const [collapsedMap, setCollapsedMap] = useState<CollapsedMap>(() =>
    loadCollapsedMap(storageKey),
  );

  const update = useCallback(
    // eslint-disable-next-line no-unused-vars
    (updater: (prev: CollapsedMap) => CollapsedMap) => {
      setCollapsedMap((prev) => {
        const next = updater(prev);
        if (next === prev) return prev;
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* localStorage が使えない環境では開閉状態を永続化しない */
        }
        return next;
      });
    },
    [storageKey],
  );

  const isCollapsed = useCallback(
    (sectionId: string) => collapsedMap[sectionId] ?? defaultCollapsed,
    [collapsedMap, defaultCollapsed],
  );

  const toggle = useCallback(
    (sectionId: string) => {
      update((prev) => ({
        ...prev,
        [sectionId]: !(prev[sectionId] ?? defaultCollapsed),
      }));
    },
    [update, defaultCollapsed],
  );

  /** 閉じていれば開く（選択行を見せるための自動展開用） */
  const expand = useCallback(
    (sectionId: string) => {
      update((prev) =>
        (prev[sectionId] ?? defaultCollapsed)
          ? { ...prev, [sectionId]: false }
          : prev,
      );
    },
    [update, defaultCollapsed],
  );

  return { isCollapsed, toggle, expand };
};

export default useCollapsedSections;
